import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";
import type Account from "byzantine/src/Account";
import { parse } from "date-fns";
import type { ChartDatum } from "../../Charts/types";

export const NET_WORTH_REPORT_QUERY_KEY = "netWorthReport";

/** Matches the `period` query param net_worth_over_time() accepts on the backend. */
export type NetWorthPeriod = "semiannual" | "nine_month" | "annual";

type NetWorthReportResponse = {
  report: {
    data: {
      net_worth: number;
      change_this_month: number | null;
      external_tracking_started_at: string | null;
      monthly_totals: { month: string; label: string; total: number }[];
      available_periods: NetWorthPeriod[];
    };
  };
};

const parseMonth = (month: string): Date | null => {
  const parsed = parse(`${month}-01`, "yyyy-MM-dd", new Date());
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export type NetWorthTrendData = {
  chartData: ChartDatum[];
  windowStart: Date;
  windowEnd: Date;
  changeThisMonth: number | null;
  externalTrackingStartedAt: Date | null;
  /**
   * Tab periods the user has enough history for (see the backend's
   * _available_net_worth_periods) - callers hide the rest, since they'd
   * just show a duplicate of a shorter period's chart.
   */
  availablePeriods: NetWorthPeriod[];
  isLoading: boolean;
  error: unknown;
};

const buildNetWorthReportUrl = (
  period: NetWorthPeriod,
  accountIds: string[],
  externalAccountIds: string[],
  isScoped: boolean
): string => {
  const params = [`period=${period}`];
  // Both params are sent together whenever isScoped, even if one is empty -
  // omitting either would mean "unrestricted" for that type server-side.
  if (isScoped) {
    params.push(`account_ids=${accountIds.join(",")}`);
    params.push(`external_account_ids=${externalAccountIds.join(",")}`);
  }
  return `reports/net_worth?${params.join("&")}`;
};

export const useNetWorthTrendData = (
  period: NetWorthPeriod,
  accountIds: string[] = [],
  externalAccountIds: string[] = [],
  isFilterActive = false
): NetWorthTrendData => {
  const isScoped =
    isFilterActive || accountIds.length > 0 || externalAccountIds.length > 0;

  const { data, isLoading, error } = useQuery<NetWorthReportResponse>({
    queryKey: [
      NET_WORTH_REPORT_QUERY_KEY,
      period,
      accountIds,
      externalAccountIds,
      isScoped,
    ],
    queryFn: () =>
      ApiHttp.fetch<NetWorthReportResponse>(
        buildNetWorthReportUrl(period, accountIds, externalAccountIds, isScoped)
      ),
    staleTime: 60_000,
    retry: 3,
  });

  const report = data?.report.data;

  const chartData = useMemo<ChartDatum[]>(() => {
    if (!report) return [];

    return report.monthly_totals.reduce((points, { month, total }) => {
      const date = parseMonth(month);
      if (date) points.push({ x: date, y: total });
      return points;
    }, [] as ChartDatum[]);
  }, [report]);

  const now = new Date();

  return {
    chartData,
    windowStart: chartData[0]?.x ?? now,
    windowEnd: chartData[chartData.length - 1]?.x ?? now,
    changeThisMonth: report?.change_this_month ?? null,
    externalTrackingStartedAt: report?.external_tracking_started_at
      ? new Date(report.external_tracking_started_at)
      : null,
    availablePeriods: report?.available_periods ?? [],
    isLoading,
    error,
  };
};

export type AccountMonthlyTotals = {
  accountId: string;
  /** Keyed by the "yyyy-MM" month string the report already uses. */
  totalsByMonth: Record<string, number>;
};

/**
 * Fetches each account's monthly history individually, one request per
 * account — the `net_worth` report only returns one aggregated total per
 * request.
 */
export const useNetWorthAccountTrends = (
  period: NetWorthPeriod,
  accounts: Account[],
  enabled: boolean
): { data: AccountMonthlyTotals[]; isLoading: boolean; hasError: boolean } => {
  const results = useQueries({
    queries: accounts.map((account) => ({
      queryKey: [NET_WORTH_REPORT_QUERY_KEY, period, "account", account.id],
      queryFn: () =>
        ApiHttp.fetch<NetWorthReportResponse>(
          account.isInternal()
            ? buildNetWorthReportUrl(period, [account.id], [], true)
            : buildNetWorthReportUrl(period, [], [account.id], true)
        ),
      enabled,
      staleTime: 60_000,
      retry: 3,
    })),
  });

  const isLoading = enabled && results.some((result) => result.isLoading);
  // Exposed separately since a failed account's totals just come back empty
  // below, with no other signal that something's missing.
  const hasError = enabled && results.some((result) => result.isError);

  const data = useMemo<AccountMonthlyTotals[]>(
    () =>
      results.map((result, index) => ({
        accountId: accounts[index].id,
        totalsByMonth: Object.fromEntries(
          (result.data?.report.data.monthly_totals ?? []).map(
            ({ month, total }) => [month, total]
          )
        ),
      })),
    [results, accounts]
  );

  return { data, isLoading, hasError };
};
